import { FC } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { observer } from 'mobx-react-lite';

import { useTrans } from '@libs/i18n';
import {
  FilesIcon,
  type Icon,
  SparkleIcon,
  StackIcon,
} from '@phosphor-icons/react';

import { ORG_TOKENS } from '../utils/tokens';

interface MiniCardProps {
  icon: Icon;
  title: string;
  body: string;
}

const MiniCard: FC<MiniCardProps> = observer(({ icon: IconCmp, title, body }) => (
  <Box
    sx={{
      p: { xs: '14px 16px', sm: '18px 20px' },
      borderRadius: `${ORG_TOKENS.radiusMd}px`,
      // Translucent white over the gradient, matching the design's glassy cards.
      backgroundColor: 'rgba(255,255,255,0.78)',
      backdropFilter: 'blur(4px)',
      border: 1,
      borderColor: 'divider',
    }}
  >
    <Box
      sx={{
        width: 34,
        height: 34,
        borderRadius: `${ORG_TOKENS.radiusSm}px`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'blue.700',
        backgroundColor: 'background.paper',
        border: 1,
        borderColor: 'blue.200',
        mb: 1.5,
      }}
    >
      <IconCmp size={16} />
    </Box>
    <Typography
      variant="textSM_semiBold"
      color="text.primary"
      sx={{ display: 'block', mb: 0.625 }}
    >
      {title}
    </Typography>
    <Typography
      variant="textXS_regular"
      color="text.secondary"
      sx={{ display: 'block', lineHeight: 1.55 }}
    >
      {body}
    </Typography>
  </Box>
));

/**
 * "Why we ask for this" educational intro — an always-visible top block that
 * grounds the page (and the LLM) in why we collect this data. Mirrors the
 * design's `EducationalIntro`: gradient panel, blue accent, three explainer
 * cards.
 */
export const ProfileHero: FC = observer(() => {
  const { t } = useTrans();
  const theme = useTheme();

  return (
    <Box
      sx={{
        position: 'relative',
        overflow: 'hidden',
        p: { xs: 2.5, md: 4 },
        borderRadius: `${ORG_TOKENS.radiusLg}px`,
        // Horizontal wash: white on the left smoothly fading to the blue tint
        // on the right, identical in LTR and Arabic. A single two-stop fade
        // (no repeated white stop) avoids any hard line between white and tint.
        // Percentage-free + fixed `to right`: the RTL stylis plugin corrupts
        // gradient stop percentages but leaves this untouched, so it is NOT
        // mirrored.
        background: 'linear-gradient(to right, #FFFFFF, #F1F7FE)',
        border: 1,
        borderColor: 'blue.200',
      }}
    >
      {/* Decorative blue glow, top-trailing corner. */}
      <Box
        sx={{
          position: 'absolute',
          top: -60,
          insetInlineEnd: -40,
          width: 460,
          height: 460,
          pointerEvents: 'none',
          // Percentage-free so the RTL plugin doesn't mirror the stops (which
          // would corrupt the glow); position mirrors via insetInlineEnd.
          // Several gradually-fading alpha stops give a smooth bloom with no
          // perceptible ring/edge where the blue meets the white.
          background:
            'radial-gradient(circle, rgba(33,120,239,0.18), rgba(33,120,239,0.12), rgba(33,120,239,0.06), rgba(33,120,239,0.02), transparent)',
        }}
      />

      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.25,
        }}
      >
        <SparkleIcon size={16} color={theme.palette.blue[700]} />
        <Typography
          variant="textXS_bold"
          color="blue.700"
          sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}
        >
          {t('app.orgProfile.hero.eyebrow')}
        </Typography>
      </Box>

      <Typography
        variant="text2XL_bold"
        color="text.primary"
        sx={{ position: 'relative', lineHeight: 1.25, maxWidth: 780 }}
      >
        {t('app.orgProfile.hero.title')}
      </Typography>

      <Box
        sx={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 2,
          mt: 3.25,
        }}
      >
        <MiniCard
          icon={StackIcon}
          title={t('app.orgProfile.hero.card1.title')}
          body={t('app.orgProfile.hero.card1.body')}
        />
        <MiniCard
          icon={SparkleIcon}
          title={t('app.orgProfile.hero.card2.title')}
          body={t('app.orgProfile.hero.card2.body')}
        />
        <MiniCard
          icon={FilesIcon}
          title={t('app.orgProfile.hero.card3.title')}
          body={t('app.orgProfile.hero.card3.body')}
        />
      </Box>
    </Box>
  );
});
